
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import RichTextField from '@/components/RichTextField/richTextField.vue'
import RiskLevelSelect from '@/views/Project/Form/components/riskLevelSelect.vue'
import { ConfirmationInput } from '../viewModels/confirmationInput'
import { DocumentInput } from '../viewModels/documentInput'
import { Input } from '../viewModels/input'
import { Quiz } from '../viewModels/quiz'
import { TextInput } from '../viewModels/textInput'
import { YesNoInput } from '../viewModels/yesNoInput'
import { MultipleChoiceInput } from '../viewModels/multipleChoiceInput'
import { defaultRichTextValue } from '@/components/Editor/Editor'
import AddDependentInputButton from './addDependentInputButton.vue'
import RichText from './richText.vue'
import ScoreLimit from './scoreLimit.vue'
import { getRiskScoreColor } from '../utils'
import { openDialog } from '@/_components/dialog/dialog'
import DeleteInputConfirmationDialog from './deleteInputConfirmationDialog.vue'
import type { Step, StepInput } from '../types/form'

type StepItem = {
    type: Input | Quiz
    value: typeof Input | typeof Quiz
}

@Component({
    components: {
        AddDependentInputButton,
        RichTextField,
        RiskLevelSelect,
        RichText,
        ScoreLimit,
    },
})
export default class FormStepContent extends Vue {
    @Prop({ type: Object, required: true }) step!: Step
    @Prop({ type: Boolean, required: true }) isWritable!: boolean
    @Prop({ type: Boolean, required: true }) isMultipleSteps!: boolean
    @Prop({ type: Boolean, required: true }) supportScoring!: boolean
    @Prop({ type: String, required: true }) language!: string
    @Prop({ type: String, required: true }) otherLanguage!: string
    @Prop({ type: Array, required: true }) editorJSBlocks!: string[]
    @Prop({ type: Boolean }) preActivationValidate!: boolean
    @Prop({ type: Boolean }) useCustomScoring!: boolean

    stepDeleteConfirmationDialogOpen = false
    idToInput: Record<string, any> = {}

    defaultRichTextValue(value?: string) {
        return defaultRichTextValue(value)
    }

    get stepItems(): StepItem[] {
        return [
            { type: new TextInput(this.language), value: TextInput },
            { type: new DocumentInput(this.language), value: DocumentInput },
            { type: new MultipleChoiceInput(this.language), value: MultipleChoiceInput },
            { type: new ConfirmationInput(this.language), value: ConfirmationInput },
            { type: new YesNoInput(this.language), value: YesNoInput },
            { type: new Quiz(this.language), value: Quiz },
        ]
    }

    get inputHintOptions(): { value: StepInput['renderHint']; text: string }[] {
        return [
            {
                value: 'radio',
                text: this.$t(`form_fe['revision.inputHint.text_radio']`) as string,
            },
            {
                value: 'dropdown',
                text: this.$t(`form_fe['revision.inputHint.text_dropdown']`) as string,
            },
        ]
    }

    get isTextRequired() {
        if (this.preActivationValidate && Boolean(this.step.text?.[this.otherLanguage])) return true

        return (
            (!this.step.inputs || this.step.inputs.length === 0) &&
            (!this.step.quiz || this.step.quiz.length === 0)
        )
    }

    get rules() {
        return {
            required: (value: string) => !!value || '',
        }
    }

    @Watch('step', { deep: true, immediate: true })
    onStepChanged() {
        this.idToInput = {}
        this.step.inputs?.forEach((input) => {
            this.idToInput[input.id] = input
            input.items?.forEach((item) => {
                // @ts-ignore -- add input id to option on which it depends
                item._inputId = input.id
                this.idToInput[item.id] = item
            })
        })
    }

    getRiskScoreColor = getRiskScoreColor

    addStepItem(item: StepItem) {
        this.$emit('add:stepItem', item.value)
        this.$nextTick(() => {
            const lastStepItem = this.$refs.stepItems?.[this.$refs.stepItems.length - 1]
            if (!lastStepItem) return

            lastStepItem.focus()
            lastStepItem.scrollIntoView()
            const stepItemContainer = lastStepItem.$el?.closest('li[data-id]')
            const highlightClasses = ['tw-outline-blue-500', 'tw-outline', 'tw-outline-offset-2']
            stepItemContainer?.classList.add(...highlightClasses)
            setTimeout(() => {
                stepItemContainer?.classList.remove(...highlightClasses)
            }, 2000)
        })
    }

    addInputItemOption(inputItem: Input) {
        inputItem.addItem()
        this.$nextTick(() => {
            const newInputItemOption = this.$refs.inputItems?.[this.$refs.inputItems.length - 1]
            newInputItemOption?.focus()
        })
    }

    addNewQuizAnswer(quiz: Quiz) {
        quiz.addAnswer()
        this.$nextTick(() => {
            const newAnswer = this.$refs.quizAnswers?.[this.$refs.quizAnswers.length - 1]
            newAnswer?.focus()
        })
    }

    handleStepTitleChanged(value: string) {
        if (this.step.title) {
            this.step.title[this.language] = value
        } else {
            this.$set(this.step, 'title', {
                [this.language]: value,
            })
        }
    }

    inputWeightRules(value: string) {
        let valueAsNumber = Number(value)

        if (valueAsNumber < 0.1 || valueAsNumber > 10) {
            return this.$t(`form_fe['revision.error_weightNotInRange']`, {
                min: 0.1,
                max: 10,
            })
        } else if (valueAsNumber % 1 !== 0) {
            // count decimal places
            let [, decimalPlaces] = value?.split('.') || []
            if (decimalPlaces.length > 2) {
                return this.$t(`form_fe['revision.error_weightMaxDecimalPlaces']`, {
                    count: 2,
                })
            }
        }

        return true
    }

    async promptDeleteInput(inputItem: Input, id: string, deleteInputFn: () => void) {
        if (!this.step?.inputs) return

        const hasDependentInputs = this.step.inputs.some((input) => {
            return (
                input.dependsOn === id ||
                inputItem.items?.some((item) => input.dependsOn === item.id)
            )
        })

        if (!hasDependentInputs) {
            deleteInputFn()
            return
        }

        const action = await openDialog(DeleteInputConfirmationDialog, {
            title: this.$t(`form_fe['revision.deleteStepItem.confirmationDialogTitle']`),
            message: this.$t(`form_fe['revision.deleteStepItem.confirmationDialogMessage']`),
        })

        if (action === false) {
            return
        }

        deleteInputFn()

        if (action === 'keep') {
            // remove dependencies
            this.step.inputs.forEach((input) => {
                if (
                    input.dependsOn === id ||
                    inputItem.items?.some((item) => input.dependsOn === item.id)
                ) {
                    input.dependsOn = null
                }
            })
        } else if (action === 'remove') {
            // remove other inputs that depend on this input or input options
            const ids = [id]
            while (ids.length) {
                const id = ids.pop()
                this.step.inputs = this.step.inputs?.filter((input) => {
                    if (
                        input.dependsOn === id ||
                        inputItem.items?.some((item) => input.dependsOn === item.id)
                    ) {
                        ids.push(input.id)
                        input.items?.forEach((item) => ids.push(item.id))
                        delete this.idToInput[input.id]
                        return false
                    }
                    return true
                })
            }
        }
    }

    async deleteStepInput(inputItem: Input) {
        this.promptDeleteInput(inputItem, inputItem.id, () => {
            this.step.inputs = this.step.inputs?.filter((input) => input.id !== inputItem.id)
        })
    }

    async removeInputItemOption(inputItem: Input, optionId: string) {
        this.promptDeleteInput(inputItem, optionId, () => {
            inputItem.removeItem(optionId)
        })
    }

    getStepInputHeading(id: string) {
        let input = this.idToInput[id]
        if (!input) return ''

        // id can be id of an option, not an input, so we need to find the input
        if (input._inputId) input = this.idToInput[input._inputId]
        return this.$t(input.name)
    }

    getInputAnswerText(id: string) {
        let input = this.idToInput[id]
        if (!input?._inputId) return ''
        return input.text?.[this.language]
    }

    getInputOptionItems(id: string) {
        let input = this.idToInput[id]
        // get options from the input on which the option depends
        if (input?._inputId) {
            input = this.idToInput[input._inputId]
            return input.items
        }

        return []
    }

    goToInput(id: string) {
        const input = document.querySelector(`[data-id="${id}"]`)
        if (!input) return

        input.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const classes = ['tw-outline-blue-500', 'tw-outline', 'tw-outline-offset-2']
        input.classList.add(...classes)
        setTimeout(() => input.classList.remove(...classes), 2000)
    }

    areDependentInputs(itemToMove: Input, nextItem: Input) {
        if (itemToMove.id === nextItem.dependsOn) return true

        if (!nextItem.dependsOn) return false

        const input = this.idToInput[nextItem.dependsOn]
        // check for temporary "_inputId" which is not part of Input
        return input ? itemToMove.id === input._inputId : false
    }

    moveStepInputUp(index: number) {
        if (!this.step.inputs || index === 0) return

        let itemToMove = this.step.inputs[index]
        let previousItem = this.step.inputs[index - 1]

        // if item to move depends on the previous item, we can't swap them
        if (this.areDependentInputs(previousItem, itemToMove)) return

        this.step.inputs[index - 1] = this.step.inputs[index]
        this.step.inputs[index] = previousItem

        // make it reactive after swapping
        this.step.inputs = [...this.step.inputs]
    }

    moveStepInputDown(index: number) {
        if (!this.step.inputs || index === this.step.inputs.length - 1) return

        let itemToMove = this.step.inputs[index]
        let nextIndex = index + 1
        let nextItem = this.step.inputs[nextIndex]

        if (this.areDependentInputs(itemToMove, nextItem)) {
            // dependent input is below the input on which it depends
            // find next input that is not a dependent of the input we are moving
            while (this.areDependentInputs(itemToMove, nextItem)) {
                nextIndex++
                nextItem = this.step.inputs[nextIndex]
            }

            // if item found, delete it from array
            if (nextItem) {
                this.step.inputs.splice(nextIndex, 1)

                // insert it back to the original position
                this.step.inputs.splice(index, 0, nextItem)
            }
        } else {
            this.step.inputs[index + 1] = this.step.inputs[index]
            this.step.inputs[index] = nextItem

            // make it reactive after swapping
            this.step.inputs = [...this.step.inputs]
        }
    }
}
